import React from "react";
import { useFormikContext } from "formik";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Collapse,
  TextField,
  Tooltip,
  IconButton,
} from "@mui/material";
import { Info as InfoIcon } from "@mui/icons-material";
import { OwnershipType } from "../../../types/ownership";

interface OwnerInfoFormProps {
  ownershipType: OwnershipType;
  entityType?: string; // "vehicle" or "trailer" 
  TextFieldComponent?: React.ComponentType<any>; // Allow custom text field component
}

// Generic text field component that can be replaced
const GenericTextField: React.FC<{
  name: string;
  label: string;
  placeholder?: string;
  tooltip?: string;
}> = ({ name, label, placeholder, tooltip }) => {
  const { values, errors, touched, handleChange, handleBlur } = useFormikContext<any>();
  
  // Helper function to get nested values safely  
  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  // Helper function to safely convert error to string
  const safeErrorToString = (error: any): string => {
    if (typeof error === 'string') return error;
    if (typeof error === 'object' && error !== null) {
      return Object.values(error).join(', ');
    }
    return '';
  };

  const value = getNestedValue(values, name) || '';
  const error = getNestedValue(errors, name);
  const isTouched = getNestedValue(touched, name);
  
  return (
    <TextField
      fullWidth
      name={name}
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      error={Boolean(error && isTouched)}
      helperText={error && isTouched ? safeErrorToString(error) : ''}
      InputProps={{
        endAdornment: tooltip && (
          <Tooltip title={tooltip} arrow>
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      }}
    />
  );
};

const OwnerInfoForm: React.FC<OwnerInfoFormProps> = ({ 
  ownershipType, 
  entityType = "asset", 
  TextFieldComponent = GenericTextField 
}) => {
  // Only show when ownership type is NotCompanyOwned
  const shouldShow = ownershipType === OwnershipType.NotCompanyOwned;

  if (!shouldShow) {
    return null;
  }

  return (
    <Collapse in={shouldShow}>
      <Card sx={{ mt: 2, mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Owner Information
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please provide detailed information about the {entityType} owner.
          </Typography>

          <Grid container spacing={2}>
            {/* Company Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Company Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldComponent
                name="ownerInfo.company"
                label="Company Name"
                placeholder="e.g., Independent Owner LLC"
                tooltip="Legal company name of the owner"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldComponent
                name="ownerInfo.ssnFein"
                label="SSN/FEIN"
                placeholder="e.g., 12-3456789 or 12-3456789"
                tooltip="Social Security Number or Federal Employer Identification Number"
              />
            </Grid>

            {/* Personal Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, mt: 2 }}>
                Personal Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldComponent
                name="ownerInfo.firstName"
                label="First Name"
                placeholder="e.g., John"
                tooltip="Owner's first name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldComponent
                name="ownerInfo.lastName"
                label="Last Name"
                placeholder="e.g., Smith"
                tooltip="Owner's last name"
              />
            </Grid>

            {/* Address Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, mt: 2 }}>
                Address Information
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextFieldComponent
                name="ownerInfo.address"
                label="Street Address"
                placeholder="e.g., 123 Main Street"
                tooltip="Complete street address"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldComponent
                name="ownerInfo.city"
                label="City"
                placeholder="e.g., New York"
                tooltip="City name"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextFieldComponent
                name="ownerInfo.state"
                label="State"
                placeholder="e.g., NY"
                tooltip="Two-letter state code"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextFieldComponent
                name="ownerInfo.zipCode"
                label="Zip Code"
                placeholder="e.g., 10001"
                tooltip="ZIP or postal code"
              />
            </Grid>

            {/* Financial Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, mt: 2 }}>
                Financial Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldComponent
                name="ownerInfo.bankAccount"
                label="Bank Account"
                placeholder="e.g., 9876543210"
                tooltip="Bank account number for payments"
              />
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, mt: 2 }}>
                Contact Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldComponent
                name="ownerInfo.contacts"
                label="Phone Number"
                placeholder="e.g., 555-123-4567"
                tooltip="Primary contact phone number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldComponent
                name="ownerInfo.email"
                label="Email Address"
                placeholder="e.g., john.smith@example.com"
                tooltip="Primary email address for communications"
              />
            </Grid>
            <Grid item xs={12}>
              <TextFieldComponent
                name="ownerInfo.website"
                label="Website"
                placeholder="e.g., www.example.com"
                tooltip="Company or personal website (optional)"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Collapse>
  );
};

export default OwnerInfoForm;
